import axios, { AxiosRequestConfig, CancelToken } from 'axios';
import { applyAuthenticationHeader, defaultResponseInterceptor, authResponseFailureInterceptor } from '@/api/interceptors/auth-interceptors';
import { Forbidden } from '@/api/responses/forbidden';
import { NotFound } from '@/api/responses/not-found';
import { BadRequest } from './responses/bad-request';

export abstract class Api {

    protected constructor() {
        axios.interceptors.request.use(applyAuthenticationHeader);
        axios.interceptors.response.use(defaultResponseInterceptor, authResponseFailureInterceptor);
    }

    protected async getAsync<T>(url: string, data?: any, cancellationToken: CancelToken | null = null) {
        url = this.buildUrl(url);

        const response = await axios.get(url, this.buildConfig(cancellationToken, data));

        this.throwIfRequestWasUnsuccessful(url, response);

        return response.data as T;
    }

    protected async getFileAsync(url: string, data?: any, cancellationToken: CancelToken | null = null): Promise<string> {
        url = this.buildUrl(url);
        const config = this.buildConfig(cancellationToken, data);
        config.responseType = 'blob';

        const response = await axios.get(url, config);

        this.throwIfRequestWasUnsuccessful(url, response);

        return response.data as string;
    }

    protected async postAsync<T>(url: string, data: any, cancellationToken: CancelToken | null = null): Promise<T> {

        url = this.buildUrl(url);

        const response = await axios.post(url, data, this.buildConfig(cancellationToken));

        this.throwIfRequestWasUnsuccessful(url, response);

        if (response.headers.location) {
            let location = response.headers.location as string;

            if (location.startsWith('http://')) {
                location = location.replace('http://', 'https://');
            }

            return (location as any) as T;
        }

        return response.data as T;
    }
    protected async postFileAsync<T>(url: string, data: File, cancellationToken: CancelToken | null = null): Promise<T> {
        url = this.buildUrl(url);

        const formData: FormData = new FormData();
        formData.append('file', data);

        const config = this.buildConfig(cancellationToken);
        config.headers['Content-type'] = 'multipart/form-data';

        const response = await axios.post(url, formData, this.buildConfig(cancellationToken));

        this.throwIfRequestWasUnsuccessful(url, response);

        if (response.headers.location) {
            return response.headers.location as T;
        }

        return response.data as T;
    }
    protected async putAsync<T>(url: string, data: any, cancellationToken: CancelToken | null = null) {

        url = this.buildUrl(url);

        const response = await axios.put(url, data, this.buildConfig(cancellationToken));

        this.throwIfRequestWasUnsuccessful(url, response);

        return response.data as T;
    }
    protected async patchAsync<T>(url: string, data: any, cancellationToken: CancelToken | null = null) {

        url = this.buildUrl(url);

        try {
            const response = await axios.patch(url, data, this.buildConfig(cancellationToken));

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected async deleteAsync<T>(url: string, cancellationToken: CancelToken | null = null) {

        url = this.buildUrl(url);

        try {
            const response = await axios.delete(url, this.buildConfig(cancellationToken));

            this.throwIfRequestWasUnsuccessful(url, response);

            return response.data as T;
        } catch (e) {
            throw e;
        }
    }
    protected buildUrl(url: string): string {
        let baseUrl = process.env.NODE_ENV !== 'production'
            ? `${process.env.VUE_APP_API_URL}`
            : `https://api.${window.location.host}`;

        if (!baseUrl.endsWith('/')) {
            baseUrl += '/';
        }

        return `${baseUrl}${url}`;
    }
    protected buildConfig(cancellationToken: CancelToken | null, data?: any): AxiosRequestConfig {
        const config: AxiosRequestConfig = {
            cancelToken: cancellationToken || undefined,
            headers: {
                'Accept': 'application/json',
                'Content-type': 'application/json',
            },
            params: data || undefined,
            validateStatus: (status: number) => {
                return status >= 200 && status < 300;
            },
            withCredentials: true,
        };

        return config;
    }
    private throwIfRequestWasUnsuccessful(url: string, response: any) {
        const hasError = (response && response.request.readyState === 4 && response.request.status === 0) || (response && response.response && (response.response.status < 200 || response.response.status >= 300));

        if (!hasError) {
            return;
        }

        if (response.response.status === 403) {
            throw new Forbidden(`Request to ${url} failed with a response status of: ${response.response.status}.`);
        }
        if (response.response.status === 404) {
            throw new NotFound(`Request to ${url} failed with a response status of: ${response.response.status}.`);
        }
        if (response.response.data.error) {
            throw new Error(response.response.data.error);
        }
        if (response.response.data.errors) {
            throw new BadRequest(`Request to ${url} failed with a response status of ${response.response.status}.`, response.response.data.errors);
        }

        throw new Error(`Request to ${url} failed with a response status of: ${response.response.status}.`);
    }
}
